import React, { useState, useEffect } from 'react';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement,
    PointElement, LineElement, Title, Tooltip, Legend, ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { fetchDashboardStats } from '../../services/apiService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';

ChartJS.register(
    CategoryScale, LinearScale, BarElement,
    PointElement, LineElement, Title, Tooltip, Legend, ArcElement
);

const statusColor = (status) => {
    const map = {
        completed: 'bg-success',
        pending: 'bg-warning text-dark',
        expired: 'bg-secondary',
        failed: 'bg-danger',
        active: 'bg-primary',
    };
    return map[status?.toLowerCase()] || 'bg-secondary';
};

const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';
const formatAmount = (a) => (a !== null && a !== undefined) ? `$${Number(a).toFixed(2)}` : '—';

const PERIODS = [
    { label: 'All Time',   value: '' },
    { label: 'Today',      value: 'today' },
    { label: 'This Week',  value: 'week' },
    { label: 'This Month', value: 'month' },
    { label: 'This Year',  value: 'year' },
];

const Dashboard = () => {
    const token = localStorage.getItem('accessToken');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('');

    const load = async (p) => {
        setLoading(true);
        try {
            const res = await fetchDashboardStats(token, p);
            setStats(res.data);
        } catch {
            toast.error('Failed to load dashboard stats');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(''); }, []);

    const handlePeriod = (p) => {
        setPeriod(p);
        load(p);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="spinner-border text-primary me-2" role="status"></div>
                <span className="text-muted">Loading dashboard...</span>
            </div>
        );
    }

    if (!stats) return null;

    const { stats: s, recentSessions, recentTransactions, sessionsByStatus, transactionsByStatus } = stats;

    // ── Chart: Sessions by Status (Doughnut) ────────────────────────────────
    const sessionStatusLabels = Object.keys(sessionsByStatus || {});
    const sessionStatusData = Object.values(sessionsByStatus || {});
    const sessionDoughnutData = {
        labels: sessionStatusLabels.map(l => l.charAt(0).toUpperCase() + l.slice(1)),
        datasets: [{
            data: sessionStatusData,
            backgroundColor: ['#00E5FF', '#FF006E', '#7B61FF', '#00B8D9', '#FF4D9A', '#80F2FF'],
            borderWidth: 2,
            borderColor: '#13133A',
        }],
    };

    // ── Chart: Transactions by Status (Doughnut) ────────────────────────────
    const txnStatusLabels = Object.keys(transactionsByStatus || {});
    const txnStatusData = Object.values(transactionsByStatus || {});
    const txnDoughnutData = {
        labels: txnStatusLabels.map(l => l.charAt(0).toUpperCase() + l.slice(1)),
        datasets: [{
            data: txnStatusData,
            backgroundColor: ['#00E5FF', '#FF006E', '#7B61FF', '#00B8D9', '#FF4D9A'],
            borderWidth: 2,
            borderColor: '#13133A',
        }],
    };

    // ── Chart: Recent Sessions Amount (Bar) ─────────────────────────────────
    const sessionBarData = {
        labels: recentSessions.map(s => s.sessionCode || s._id.slice(-6)),
        datasets: [{
            label: 'Session Amount (USD)',
            data: recentSessions.map(s => s.amount),
            backgroundColor: 'rgba(0, 229, 255, 0.5)',
            borderColor: 'rgba(0, 229, 255, 1)',
            borderWidth: 1,
            borderRadius: 6,
        }],
    };

    // ── Chart: Recent Transactions Amount (Bar) ──────────────────────────────
    const txnBarData = {
        labels: recentTransactions.map(t => t.invoiceId.slice(-8)),
        datasets: [{
            label: 'Transaction Amount (USD)',
            data: recentTransactions.map(t => t.amount),
            backgroundColor: 'rgba(255, 0, 110, 0.5)',
            borderColor: 'rgba(255, 0, 110, 1)',
            borderWidth: 1,
            borderRadius: 6,
        }],
    };

    const chartOptions = (title) => ({
        responsive: true,
        plugins: {
            legend: { display: false },
            title: { display: false },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: 'rgba(0,229,255,0.06)' },
                ticks: { color: '#8899CC' },
            },
            x: {
                grid: { display: false },
                ticks: { color: '#8899CC' },
            },
        },
    });

    const doughnutOptions = {
        responsive: true,
        plugins: {
            legend: { position: 'bottom', labels: { padding: 16, usePointStyle: true, color: '#8899CC' } },
        },
        cutout: '65%',
    };

    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                <div>
                    <h2 className="fw-bold mb-0">Dashboard</h2>
                    <p className="text-muted small mb-0">Platform overview and recent activity</p>
                </div>
                <div className="btn-group" role="group">
                    {PERIODS.map(p => (
                        <button
                            key={p.value}
                            type="button"
                            className={`btn btn-sm ${period === p.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => handlePeriod(p.value)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="row g-3 mb-4">
                {[
                    { label: 'Total Users',        value: s.totalUsers,        color: 'text-primary',   icon: '👥' },
                    { label: 'Total Games',         value: s.totalGames,        color: 'text-success',   icon: '🎮' },
                    { label: 'Total Sessions',      value: s.totalSessions,     color: 'text-info',      icon: '🕹️' },
                    { label: 'Total Transactions',  value: s.totalTransactions, color: 'text-warning',   icon: '💳' },
                    { label: 'Total Revenue',       value: formatAmount(s.totalRevenue), color: 'text-success', icon: '💰' },
                ].map((item, i) => (
                    <div className="col-6 col-md-4 col-lg" key={i}>
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-body">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <p className="text-muted small mb-0 text-uppercase fw-semibold">{item.label}</p>
                                    <span style={{ fontSize: '1.3rem' }}>{item.icon}</span>
                                </div>
                                <h3 className={`fw-bold mb-0 ${item.color}`}>{item.value}</h3>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Payment Breakdown */}
            <div className="mb-2">
                <h6 className="fw-bold text-muted text-uppercase small mb-2">Payment Breakdown</h6>
            </div>
            <div className="row g-3 mb-4">
                {[
                    { label: 'Settled',    value: transactionsByStatus?.settled    || 0, color: 'text-success',   bg: 'border-success', icon: '✅' },
                    { label: 'New',        value: transactionsByStatus?.new        || 0, color: 'text-primary',   bg: 'border-primary', icon: '🆕' },
                    { label: 'Processing', value: transactionsByStatus?.processing || 0, color: 'text-info',      bg: 'border-info',    icon: '⏳' },
                    { label: 'Expired',    value: transactionsByStatus?.expired    || 0, color: 'text-secondary', bg: '',               icon: '⌛' },
                    { label: 'Invalid',    value: transactionsByStatus?.invalid    || 0, color: 'text-danger',    bg: 'border-danger',  icon: '❌' },
                    { label: 'Refunded',   value: transactionsByStatus?.refunded   || 0, color: 'text-warning',   bg: '',               icon: '↩️' },
                ].map((item, i) => (
                    <div className="col-6 col-md-4 col-lg-2" key={i}>
                        <div className={`card border-0 shadow-sm h-100 border-start border-3 ${item.bg}`}>
                            <div className="card-body py-2 px-3">
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <p className="text-muted small mb-0 text-uppercase fw-semibold" style={{ fontSize: '0.65rem' }}>{item.label}</p>
                                    <span style={{ fontSize: '1rem' }}>{item.icon}</span>
                                </div>
                                <h4 className={`fw-bold mb-0 ${item.color}`}>{item.value}</h4>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts Row 1 — Bar Charts */}
            <div className="row g-3 mb-4">
                <div className="col-12 col-lg-6">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <h6 className="fw-bold mb-1">Recent Session Amounts</h6>
                            <p className="text-muted small mb-3">By session code</p>
                            <Bar data={sessionBarData} options={chartOptions()} />
                        </div>
                    </div>
                </div>
                <div className="col-12 col-lg-6">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <h6 className="fw-bold mb-1">Recent Transaction Amounts</h6>
                            <p className="text-muted small mb-3">By invoice ID (last 8 chars)</p>
                            <Bar data={txnBarData} options={chartOptions()} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Row 2 — Doughnuts */}
            <div className="row g-3 mb-4">
                <div className="col-12 col-sm-6">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body d-flex flex-column align-items-center">
                            <h6 className="fw-bold mb-1 align-self-start">Sessions by Status</h6>
                            <p className="text-muted small mb-3 align-self-start">Distribution across all sessions</p>
                            <div style={{ maxWidth: 260, width: '100%' }}>
                                <Doughnut data={sessionDoughnutData} options={doughnutOptions} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-sm-6">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body d-flex flex-column align-items-center">
                            <h6 className="fw-bold mb-1 align-self-start">Transactions by Status</h6>
                            <p className="text-muted small mb-3 align-self-start">Distribution across all transactions</p>
                            <div style={{ maxWidth: 260, width: '100%' }}>
                                <Doughnut data={txnDoughnutData} options={doughnutOptions} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Sessions Table */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-0">
                    <div className="px-3 pt-3 pb-2 border-bottom">
                        <h6 className="fw-bold mb-0">Recent Sessions</h6>
                        <p className="text-muted small mb-0">Latest {recentSessions.length} sessions</p>
                    </div>
                    {recentSessions.length === 0 ? (
                        <div className="text-center py-4 text-muted">No sessions yet</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-3">Code</th>
                                        <th>Player</th>
                                        <th className="d-none d-sm-table-cell">Game</th>
                                        <th className="d-none d-md-table-cell">Room</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th className="d-none d-lg-table-cell">Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentSessions.map(session => (
                                        <tr key={session._id}>
                                            <td className="ps-3">
                                                <span className="badge border fw-semibold" style={{ backgroundColor: '#0D0D2B', color: '#00E5FF', borderColor: 'rgba(0,229,255,0.3) !important' }}>
                                                    {session.sessionCode}
                                                </span>
                                            </td>
                                            <td className="fw-semibold small">{session.playerUsername || '—'}</td>
                                            <td className="d-none d-sm-table-cell small">{session.game?.name || '—'}</td>
                                            <td className="d-none d-md-table-cell small text-muted">{session.room?.name || '—'}</td>
                                            <td className="fw-semibold">{formatAmount(session.amount)}</td>
                                            <td>
                                                <span className={`badge ${statusColor(session.status)}`}>
                                                    {session.status}
                                                </span>
                                            </td>
                                            <td className="d-none d-lg-table-cell text-muted small">{formatDate(session.createdAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Recent Transactions Table */}
            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    <div className="px-3 pt-3 pb-2 border-bottom">
                        <h6 className="fw-bold mb-0">Recent Transactions</h6>
                        <p className="text-muted small mb-0">Latest {recentTransactions.length} transactions</p>
                    </div>
                    {recentTransactions.length === 0 ? (
                        <div className="text-center py-4 text-muted">No transactions yet</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-3">Invoice ID</th>
                                        <th>Player</th>
                                        <th>Amount</th>
                                        <th className="d-none d-sm-table-cell">Currency</th>
                                        <th>Status</th>
                                        <th className="d-none d-md-table-cell">Expires</th>
                                        <th className="d-none d-lg-table-cell">Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentTransactions.map(txn => (
                                        <tr key={txn._id}>
                                            <td className="ps-3">
                                                <a
                                                    href={txn.invoiceUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-decoration-none small fw-semibold"
                                                >
                                                    {txn.invoiceId.slice(0, 12)}…
                                                </a>
                                            </td>
                                            <td className="fw-semibold small">{txn.playerUsername || '—'}</td>
                                            <td className="fw-semibold">{formatAmount(txn.amount)}</td>
                                            <td className="d-none d-sm-table-cell text-muted small">{txn.currency}</td>
                                            <td>
                                                <span className={`badge ${statusColor(txn.status)}`}>
                                                    {txn.status}
                                                </span>
                                            </td>
                                            <td className="d-none d-md-table-cell text-muted small">{formatDate(txn.expiresAt)}</td>
                                            <td className="d-none d-lg-table-cell text-muted small">{formatDate(txn.createdAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;